import { create } from 'zustand';
import { bossBabyChatLogApi } from '../apis/bossBabyChatLog';
import type {
  BossBabyChatSessionDto,
  BossBabyChatMessageDto,
} from '../apis/bossBabyChatLog';

interface BossBabyChatLogState {
  sessions: BossBabyChatSessionDto[];
  selectedSession: BossBabyChatSessionDto | null;
  currentSession: BossBabyChatSessionDto | null;
  messages: BossBabyChatMessageDto[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

interface BossBabyChatLogActions {
  fetchSessions: (params?: {
    page?: number;
    limit?: number;
    comId?: number;
    status?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }) => Promise<void>;
  fetchSessionDetail: (sessionUuid: string) => Promise<void>;
  fetchSessionMessages: (sessionUuid: string) => Promise<void>;
  setCurrentSession: (session: BossBabyChatSessionDto | null) => void;
  clearError: () => void;
  reset: () => void;
}

/**
 * 보스베이비 채팅 로그 Store
 *
 * @description 보스베이비 채팅 로그 상태를 관리하는 Zustand Store
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-29
 */
export const useBossBabyChatLogStore = create<
  BossBabyChatLogState & BossBabyChatLogActions
>((set, get) => ({
  // State
  sessions: [],
  selectedSession: null,
  currentSession: null,
  messages: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },

  // Actions
  fetchSessions: async (params = {}) => {
    set({ loading: true, error: null });
    try {
      const response = await bossBabyChatLogApi.getSessions(params);
      set({
        sessions: response.list,
        pagination: {
          page: response.page,
          limit: response.limit,
          total: response.count,
        },
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to fetch sessions',
        loading: false,
      });
    }
  },

  fetchSessionDetail: async (sessionUuid: string) => {
    set({ loading: true, error: null });
    try {
      const session = await bossBabyChatLogApi.getSessionDetail(sessionUuid);
      set({
        selectedSession: session,
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to fetch session detail',
        loading: false,
      });
    }
  },

  fetchSessionMessages: async (sessionUuid: string) => {
    set({ loading: true, error: null });
    try {
      const session = await bossBabyChatLogApi.getSessionDetail(sessionUuid);
      set({
        currentSession: session,
        messages: session.messages || [],
        loading: false,
      });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to fetch session messages',
        loading: false,
      });
    }
  },

  setCurrentSession: (session: BossBabyChatSessionDto | null) => {
    set({ currentSession: session });
  },

  clearError: () => set({ error: null }),

  reset: () =>
    set({
      sessions: [],
      selectedSession: null,
      currentSession: null,
      messages: [],
      loading: false,
      error: null,
      pagination: { page: 1, limit: 10, total: 0 },
    }),
}));









